/**
 * ===================================================
 * Module: frontend.src.components.PDFViewer.tsx
 * Description: PDF Viewer component for displaying PDF and redaction boxes
 * Owner: Kawada Technosystem Co Ltd
 * All Rights Reserved
 * This script is proprietary and confidential.
 * Unauthorized copying, distribution, or modification of this
 * file, via any medium, is strictly prohibited.
 * ===================================================
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { RedactionBox, PDFViewerProps, PageMetadata } from '../types';

const PDFViewer: React.FC<PDFViewerProps> = ({
  pdfDoc,
  pageNum,
  scale,
  redactionBoxes,
  pageMetadata,
  isDrawing,
  isDrawMode,
  selectedBoxIds,
  onPageRender,
  onBoxSelect,
  onBoxCreate,
  onBoxUpdate,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onError
}) => {
  const { t } = useTranslation('pdfEditor');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentViewportRef = useRef<any>(null);
  const currentPageRef = useRef<any>(null);
  const isDrawingRef = useRef(false);
  const startPosRef = useRef({ x: 0, y: 0 });
  const drawingBoxRef = useRef<HTMLDivElement | null>(null);

  // Selection and drag state
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [resizeHandle, setResizeHandle] = useState<string | null>(null);
  const [dragBoxId, setDragBoxId] = useState<string | null>(null);

  // Pan state
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0, scrollLeft: 0, scrollTop: 0 });

  // Add state to track rendering and debouncing
  const [isRendering, setIsRendering] = useState(false);
  const isRenderingRef = useRef(false);
  const renderTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const renderGenRef = useRef(0);
  const renderTaskRef = useRef<any>(null);

  // Convert backend coordinates to canvas coordinates (in CSS pixels)
  // Backend stores coordinates in MediaBox (unrotated) space.
  // PDF.js displays in rotated space. We must apply rotation transform.
  // Rotation is read from the PDF.js page object directly (not from metadata)
  // so it works even when metadata is missing (legacy save format).
  const pdfCoordsToCanvasCoords = (pdfBbox: number[], _boxPageNum: number) => {
    if (!currentViewportRef.current) {
      return pdfBbox;
    }

    try {
      const viewport = currentViewportRef.current;
      const displayScale = viewport.scale;

      // Get rotation directly from PDF.js page (always available, unlike pageMetadata)
      const rotation = currentPageRef.current?.rotate ?? 0;

      let [x0, y0, x1, y1] = pdfBbox;

      // Get page dimensions in points (rotated display dimensions)
      const pageWidthPts = viewport.width / displayScale;
      const pageHeightPts = viewport.height / displayScale;

      // Apply rotation transformation to convert from MediaBox (unrotated)
      // to display (rotated) coordinate space
      if (rotation === 90) {
        const newX0 = pageWidthPts - y1;
        const newY0 = x0;
        const newX1 = pageWidthPts - y0;
        const newY1 = x1;
        x0 = newX0; y0 = newY0; x1 = newX1; y1 = newY1;
      } else if (rotation === 180) {
        const newX0 = pageWidthPts - x1;
        const newY0 = pageHeightPts - y1;
        const newX1 = pageWidthPts - x0;
        const newY1 = pageHeightPts - y0;
        x0 = newX0; y0 = newY0; x1 = newX1; y1 = newY1;
      } else if (rotation === 270) {
        const newX0 = y0;
        const newY0 = pageHeightPts - x1;
        const newX1 = y1;
        const newY1 = pageHeightPts - x0;
        x0 = newX0; y0 = newY0; x1 = newX1; y1 = newY1;
      }
      // rotation === 0: no transformation needed

      // Scale from points to canvas CSS pixels
      const canvasX0 = x0 * displayScale;
      const canvasY0 = y0 * displayScale;
      const canvasX1 = x1 * displayScale;
      const canvasY1 = y1 * displayScale;

      // Normalize to ensure min/max ordering
      return [
        Math.min(canvasX0, canvasX1),
        Math.min(canvasY0, canvasY1),
        Math.max(canvasX0, canvasX1),
        Math.max(canvasY0, canvasY1)
      ];

    } catch (error) {
      onError?.(`Failed to convert PDF coordinates to canvas coordinates: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return pdfBbox;
    }
  };

  // Convert backend PDF coordinates to percentage positions relative to the page.
  // This is used for rendering boxes — percentage positioning is immune to
  // scale/DPR/browser-zoom mismatches because it's relative to the overlay container.
  // Rotation is read from PDF.js page object directly (always available).
  const pdfCoordsToPercentage = (pdfBbox: number[], _boxPageNum: number) => {
    if (!currentViewportRef.current) return null;

    try {
      const viewport = currentViewportRef.current;
      const displayScale = viewport.scale;

      // Get rotation directly from PDF.js page
      const rotation = currentPageRef.current?.rotate ?? 0;

      let [x0, y0, x1, y1] = pdfBbox;

      const pageWidthPts = viewport.width / displayScale;
      const pageHeightPts = viewport.height / displayScale;

      // Apply rotation transformation (MediaBox → display space)
      if (rotation === 90) {
        const newX0 = pageWidthPts - y1;
        const newY0 = x0;
        const newX1 = pageWidthPts - y0;
        const newY1 = x1;
        x0 = newX0; y0 = newY0; x1 = newX1; y1 = newY1;
      } else if (rotation === 180) {
        const newX0 = pageWidthPts - x1;
        const newY0 = pageHeightPts - y1;
        const newX1 = pageWidthPts - x0;
        const newY1 = pageHeightPts - y0;
        x0 = newX0; y0 = newY0; x1 = newX1; y1 = newY1;
      } else if (rotation === 270) {
        const newX0 = y0;
        const newY0 = pageHeightPts - x1;
        const newX1 = y1;
        const newY1 = pageHeightPts - x0;
        x0 = newX0; y0 = newY0; x1 = newX1; y1 = newY1;
      }

      // Convert to percentages relative to page dimensions in points
      const pctLeft = (Math.min(x0, x1) / pageWidthPts) * 100;
      const pctTop = (Math.min(y0, y1) / pageHeightPts) * 100;
      const pctWidth = (Math.abs(x1 - x0) / pageWidthPts) * 100;
      const pctHeight = (Math.abs(y1 - y0) / pageHeightPts) * 100;

      return { pctLeft, pctTop, pctWidth, pctHeight };
    } catch {
      return null;
    }
  };

  // Convert canvas coordinates to backend coordinates (MediaBox/unrotated format)
  // Canvas uses pixels with TOP-LEFT origin in the rotated display space.
  // Backend stores coordinates in MediaBox (unrotated) space.
  // We apply the inverse rotation transformation.
  // Rotation is read from PDF.js page object directly (always available).
  const canvasCoordsToPdfCoords = (canvasBbox: number[]): [number, number, number, number] => {
    if (!currentViewportRef.current) {
      return [canvasBbox[0], canvasBbox[1], canvasBbox[2], canvasBbox[3]];
    }

    try {
      const viewport = currentViewportRef.current;
      const displayScale = viewport.scale;

      // Get rotation directly from PDF.js page
      const rotation = currentPageRef.current?.rotate ?? 0;

      const [cx0, cy0, cx1, cy1] = canvasBbox;

      // Scale from canvas pixels to points first
      let x0 = cx0 / displayScale;
      let y0 = cy0 / displayScale;
      let x1 = cx1 / displayScale;
      let y1 = cy1 / displayScale;

      // Get page dimensions in points (rotated display dimensions)
      const pageWidthPts = viewport.width / displayScale;
      const pageHeightPts = viewport.height / displayScale;

      // Apply inverse rotation transformation (display → MediaBox space)
      if (rotation === 90) {
        const pdfX0 = y0;
        const pdfY0 = pageWidthPts - x1;
        const pdfX1 = y1;
        const pdfY1 = pageWidthPts - x0;
        x0 = pdfX0; y0 = pdfY0; x1 = pdfX1; y1 = pdfY1;
      } else if (rotation === 180) {
        const pdfX0 = pageWidthPts - x1;
        const pdfY0 = pageHeightPts - y1;
        const pdfX1 = pageWidthPts - x0;
        const pdfY1 = pageHeightPts - y0;
        x0 = pdfX0; y0 = pdfY0; x1 = pdfX1; y1 = pdfY1;
      } else if (rotation === 270) {
        const pdfX0 = pageHeightPts - y1;
        const pdfY0 = x0;
        const pdfX1 = pageHeightPts - y0;
        const pdfY1 = x1;
        x0 = pdfX0; y0 = pdfY0; x1 = pdfX1; y1 = pdfY1;
      }
      // rotation === 0: no transformation needed

      // Normalize to ensure min/max ordering
      return [
        Math.min(x0, x1),
        Math.min(y0, y1),
        Math.max(x0, x1),
        Math.max(y0, y1)
      ];

    } catch (error) {
      onError?.(`Failed to convert canvas coordinates to PDF coordinates: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return [canvasBbox[0], canvasBbox[1], canvasBbox[2], canvasBbox[3]];
    }
  };

  // Sync overlay and container size to match the canvas CSS size exactly.
  // IMPORTANT: Use the exact viewport float values (same as canvas.style.width/height)
  // rather than Math.ceil, to prevent sub-pixel size differences that cause
  // redaction box drift after repeated zoom operations.
  const syncOverlaySize = useCallback((viewport: any) => {
    const w = viewport.width + 'px';
    const h = viewport.height + 'px';

    if (overlayRef.current) {
      overlayRef.current.style.width = w;
      overlayRef.current.style.height = h;
    }
    if (containerRef.current) {
      containerRef.current.style.width = w;
      containerRef.current.style.height = h;
    }
  }, []);

  // Get mouse position relative to the overlay in CSS-pixel space,
  // compensating for browser zoom (Ctrl+scroll) which distorts getBoundingClientRect.
  const getRelativeMousePos = useCallback((e: React.MouseEvent | MouseEvent): { x: number; y: number } => {
    const el = containerRef.current ?? overlayRef.current;
    if (!el) return { x: 0, y: 0 };

    const rect = el.getBoundingClientRect();
    const viewport = currentViewportRef.current;
    if (!viewport) {
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }

    // When browser is zoomed, rect.width/height ≠ viewport.width/height.
    // We compute a scale factor to map visual pixels → CSS pixels.
    const scaleX = viewport.width / rect.width;
    const scaleY = viewport.height / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, []);

  // Render cache state
  const RENDER_CACHE_SIZE = 20;
  const renderCacheRef = useRef<Map<string, {
    imageData: ImageData,
    viewport: any,
    page: any
  }>>(new Map());

  // Render PDF page with proper debouncing and state management
  const queueRenderPage = async () => {
    if (!pdfDoc || !canvasRef.current) return;

    // Clear any pending render
    if (renderTimeoutRef.current) {
      clearTimeout(renderTimeoutRef.current);
    }

    // Bump generation so any in-flight render knows it's stale
    const thisGen = ++renderGenRef.current;

    // Debounce the render to prevent rapid successive renders
    renderTimeoutRef.current = setTimeout(async () => {
      // Use ref (not state) to avoid stale-closure reads
      if (isRenderingRef.current) return;

      // If a newer queueRenderPage call was made, abandon this one
      if (thisGen !== renderGenRef.current) return;

      const dpr = window.devicePixelRatio || 1;
      const cacheKey = `${pageNum}-${scale}-${dpr}`;
      const cached = renderCacheRef.current.get(cacheKey);

      // LRU behavior: if cached, move to end of map
      if (cached) {
        renderCacheRef.current.delete(cacheKey);
        renderCacheRef.current.set(cacheKey, cached);

        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (context) {
          const viewport = cached.viewport;

          canvas.style.width = viewport.width + 'px';
          canvas.style.height = viewport.height + 'px';
          canvas.width = cached.imageData.width;
          canvas.height = cached.imageData.height;

          context.putImageData(cached.imageData, 0, 0);

          currentViewportRef.current = viewport;
          currentPageRef.current = cached.page;

          syncOverlaySize(viewport);
          renderRedactionBoxes();
          onPageRender(pageNum);
          return;
        }
      }

      try {
        isRenderingRef.current = true;
        setIsRendering(true);

        const page = await pdfDoc.getPage(pageNum);
        if (thisGen !== renderGenRef.current) return;

        const outputScale = window.devicePixelRatio || 1;

        // Get viewport at user-scale for CSS sizing, overlay, and coordinates
        const viewport = page.getViewport({ scale });
        // Get DPR-scaled viewport for high-res rendering
        const renderViewport = page.getViewport({ scale: scale * outputScale });

        const canvas = canvasRef.current;
        if (!canvas) return;

        // Set CSS display size
        canvas.style.width = viewport.width + 'px';
        canvas.style.height = viewport.height + 'px';

        // Set canvas backing store to match DPR-scaled render viewport
        canvas.width = Math.ceil(renderViewport.width);
        canvas.height = Math.ceil(renderViewport.height);

        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return;

        // Store the user-scale viewport for coordinate conversion
        currentViewportRef.current = viewport;
        currentPageRef.current = page;

        // Sync overlay and container to match CSS-scale viewport dimensions
        syncOverlaySize(viewport);

        const renderContext = {
          canvasContext: context,
          viewport: renderViewport
        };

        // Cancel any in-flight render task
        if (renderTaskRef.current) {
          try { renderTaskRef.current.cancel(); } catch { /* ignore */ }
          renderTaskRef.current = null;
        }

        // Render the PDF page
        const renderTask = page.render(renderContext);
        renderTaskRef.current = renderTask;
        await renderTask.promise;
        renderTaskRef.current = null;

        // If a newer render was queued while PDF.js was rendering, discard
        if (thisGen !== renderGenRef.current) return;

        // Capture rendered image data for cache
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        renderCacheRef.current.set(cacheKey, { imageData, viewport, page });

        // Enforce cache size limit (LRU)
        if (renderCacheRef.current.size > RENDER_CACHE_SIZE) {
          const firstKey = renderCacheRef.current.keys().next().value;
          if (firstKey !== undefined) {
            renderCacheRef.current.delete(firstKey);
          }
        }

        // Wait a moment for the render to flush to the screen
        await new Promise(resolve => setTimeout(resolve, 50));

        // Render boxes after PDF is fully rendered
        renderRedactionBoxes();

        onPageRender(pageNum);

      } catch (error: any) {
        // If our render was cancelled (e.g. by a newer render), re-queue
        if (error?.name === 'RenderingCancelledException') {
          renderTaskRef.current = null;
          isRenderingRef.current = false;
          setIsRendering(false);
          setTimeout(() => queueRenderPage(), 50);
          return;
        }
        onError?.(`Failed to render PDF page: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        isRenderingRef.current = false;
        setIsRendering(false);
      }
    }, 100);
  };

  // Render redaction boxes
  const renderRedactionBoxes = () => {
    if (!overlayRef.current || isRendering) return;

    // Clear existing boxes
    overlayRef.current.innerHTML = '';

    Object.entries(redactionBoxes).forEach(([boxId, box]) => {
      if (box.page !== pageNum) return;

      const boxElement = document.createElement('div');
      boxElement.className = 'redaction-box';
      boxElement.dataset.boxId = boxId;

      // Use percentage-based positioning for zoom resilience.
      // Percentages are relative to the overlay container, so they're
      // immune to scale/DPR/browser-zoom mismatches.
      const pct = pdfCoordsToPercentage(box.bbox, pageNum);
      if (pct) {
        boxElement.style.left = `${pct.pctLeft}%`;
        boxElement.style.top = `${pct.pctTop}%`;
        boxElement.style.width = `${pct.pctWidth}%`;
        boxElement.style.height = `${pct.pctHeight}%`;
      } else {
        // Fallback to pixel-based positioning if viewport not ready
        const canvasCoords = pdfCoordsToCanvasCoords(box.bbox, pageNum);
        const [x1, y1, x2, y2] = canvasCoords;
        boxElement.style.left = `${x1}px`;
        boxElement.style.top = `${y1}px`;
        boxElement.style.width = `${x2 - x1}px`;
        boxElement.style.height = `${y2 - y1}px`;
      }

      const isSelected = selectedBoxIds.has(boxId);
      if (isSelected) {
        boxElement.classList.add('selected');

        // Add resize handles only in select mode and only for single selection
        if (!isDrawMode && selectedBoxIds.size === 1) {
          boxElement.innerHTML = `
            <div class="resize-handle nw" data-handle="nw"></div>
            <div class="resize-handle ne" data-handle="ne"></div>
            <div class="resize-handle sw" data-handle="sw"></div>
            <div class="resize-handle se" data-handle="se"></div>
            <div class="resize-handle n" data-handle="n"></div>
            <div class="resize-handle s" data-handle="s"></div>
            <div class="resize-handle w" data-handle="w"></div>
            <div class="resize-handle e" data-handle="e"></div>
          `;
        }
      }

      // Add drawing class if currently being drawn
      if (isDrawing && isSelected) {
        boxElement.classList.add('drawing');
      }

      overlayRef.current!.appendChild(boxElement);
    });
  };

  // Drawing functions
  const startDrawing = (x: number, y: number) => {
    if (!isDrawMode) {
      return;
    }

    isDrawingRef.current = true;
    startPosRef.current = { x, y };

    const overlay = overlayRef.current;
    if (!overlay) {
      return;
    }

    // Create temporary drawing box
    const drawingBox = document.createElement('div');
    drawingBox.className = 'redaction-box drawing';
    drawingBox.style.left = x + 'px';
    drawingBox.style.top = y + 'px';
    drawingBox.style.width = '0px';
    drawingBox.style.height = '0px';

    overlay.appendChild(drawingBox);
    drawingBoxRef.current = drawingBox;
  };

  const updateDrawing = (x: number, y: number) => {
    if (!isDrawingRef.current || !drawingBoxRef.current) return;

    const startX = startPosRef.current.x;
    const startY = startPosRef.current.y;

    const left = Math.min(startX, x);
    const top = Math.min(startY, y);
    const width = Math.abs(x - startX);
    const height = Math.abs(y - startY);

    drawingBoxRef.current.style.left = left + 'px';
    drawingBoxRef.current.style.top = top + 'px';
    drawingBoxRef.current.style.width = width + 'px';
    drawingBoxRef.current.style.height = height + 'px';
  };

  const finishDrawing = (x: number, y: number) => {
    if (!isDrawingRef.current || !drawingBoxRef.current) return;

    const startX = startPosRef.current.x;
    const startY = startPosRef.current.y;

    const left = Math.min(startX, x);
    const top = Math.min(startY, y);
    const width = Math.abs(x - startX);
    const height = Math.abs(y - startY);

    if (width > 5 && height > 5) {
      const canvasBbox = [left, top, left + width, top + height];
      const pdfBbox = canvasCoordsToPdfCoords(canvasBbox);

      const newBox: RedactionBox = {
        bbox: pdfBbox,
        page: pageNum
      };

      onBoxCreate(newBox);
    }

    // Remove temporary drawing box
    drawingBoxRef.current.remove();
    drawingBoxRef.current = null;
    isDrawingRef.current = false;
  };

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const { x, y } = getRelativeMousePos(e);

    // Check if clicking on a resize handle
    const target = e.target as HTMLElement;
    if (target.classList.contains('resize-handle')) {
      // Only allow resize in select mode
      if (!isDrawMode) {
        setIsResizing(true);
        setResizeHandle(target.dataset.handle || null);
        const closestBox = target.closest('.redaction-box') as HTMLElement;
        setDragBoxId(closestBox?.dataset.boxId || null);
        setDragStart({ x, y });
      }
      return;
    }

    // Check if clicking on a redaction box
    const boxElement = target.closest('.redaction-box') as HTMLElement;
    if (boxElement && !boxElement.classList.contains('drawing')) {
      const boxId = boxElement.dataset.boxId;
      if (boxId) {

        if (isDrawMode) {
          return;
        }

        onBoxSelect(boxId, e.ctrlKey || e.metaKey);
        // Only start dragging if not using ctrl key (multi-select mode)
        if (!e.ctrlKey && !e.metaKey) {
          setIsDragging(true);
          setDragBoxId(boxId);
          setDragStart({ x, y });
        }
        return;
      }
    }

    // If drawing mode, create new box
    if (isDrawMode) {
      startDrawing(x, y);
    } else {
      // If not in draw mode and clicking outside boxes, deselect all
      if (selectedBoxIds.size > 0) {
        onBoxSelect('', false);
      }
    }

    // Handle Pan in non-draw mode (clicking on empty canvas area)
    if (!isDrawMode) {
      setIsPanning(true);
      const container = document.querySelector('.pdf-viewer-container');
      if (container) {
        setPanStart({
          x: e.clientX,
          y: e.clientY,
          scrollLeft: container.scrollLeft,
          scrollTop: container.scrollTop
        });
      }
      return;
    }

    onMouseDown(x, y);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    e.preventDefault();
    const { x, y } = getRelativeMousePos(e);

    if (isPanning) {
      const container = document.querySelector('.pdf-viewer-container');
      if (container) {
        const dx = e.clientX - panStart.x;
        const dy = e.clientY - panStart.y;
        container.scrollLeft = panStart.scrollLeft - dx;
        container.scrollTop = panStart.scrollTop - dy;
      }
      return;
    }

    if (isDrawingRef.current) {
      updateDrawing(x, y);
    } else if (isDragging && dragBoxId && !isDrawMode) {
      // Handle box dragging only when not in draw mode.
      // Convert pixel deltas to percentage deltas relative to the overlay,
      // so dragging works correctly with percentage-positioned boxes.
      const deltaX = x - dragStart.x;
      const deltaY = y - dragStart.y;

      const boxElement = overlayRef.current?.querySelector(`[data-box-id="${dragBoxId}"]`) as HTMLElement;
      if (boxElement && overlayRef.current) {
        const overlayW = overlayRef.current.offsetWidth;
        const overlayH = overlayRef.current.offsetHeight;

        // Read current resolved pixel position (works regardless of px or % units)
        const currentLeft = boxElement.offsetLeft;
        const currentTop = boxElement.offsetTop;

        // Apply pixel delta, then convert back to percentage
        const newLeftPct = ((currentLeft + deltaX) / overlayW) * 100;
        const newTopPct = ((currentTop + deltaY) / overlayH) * 100;

        boxElement.style.left = `${newLeftPct}%`;
        boxElement.style.top = `${newTopPct}%`;

        setDragStart({ x, y });
      }
    } else if (isResizing && dragBoxId && resizeHandle && !isDrawMode) {
      // Handle box resizing only when not in draw mode.
      // Convert pixel deltas to percentage deltas for zoom-resilient resizing.
      const boxElement = overlayRef.current?.querySelector(`[data-box-id="${dragBoxId}"]`) as HTMLElement;
      if (boxElement && overlayRef.current) {
        const overlayW = overlayRef.current.offsetWidth;
        const overlayH = overlayRef.current.offsetHeight;

        // Read resolved pixel positions
        const currentLeft = boxElement.offsetLeft;
        const currentTop = boxElement.offsetTop;
        const currentWidth = boxElement.offsetWidth;
        const currentHeight = boxElement.offsetHeight;

        let newLeft = currentLeft;
        let newTop = currentTop;
        let newWidth = currentWidth;
        let newHeight = currentHeight;

        const deltaX = x - dragStart.x;
        const deltaY = y - dragStart.y;

        switch (resizeHandle) {
          case 'nw':
            newLeft = currentLeft + deltaX;
            newTop = currentTop + deltaY;
            newWidth = currentWidth - deltaX;
            newHeight = currentHeight - deltaY;
            break;
          case 'ne':
            newTop = currentTop + deltaY;
            newWidth = currentWidth + deltaX;
            newHeight = currentHeight - deltaY;
            break;
          case 'sw':
            newLeft = currentLeft + deltaX;
            newWidth = currentWidth - deltaX;
            newHeight = currentHeight + deltaY;
            break;
          case 'se':
            newWidth = currentWidth + deltaX;
            newHeight = currentHeight + deltaY;
            break;
          case 'n':
            newTop = currentTop + deltaY;
            newHeight = currentHeight - deltaY;
            break;
          case 's':
            newHeight = currentHeight + deltaY;
            break;
          case 'w':
            newLeft = currentLeft + deltaX;
            newWidth = currentWidth - deltaX;
            break;
          case 'e':
            newWidth = currentWidth + deltaX;
            break;
        }

        // Ensure minimum size (10px)
        if (newWidth < 10) newWidth = 10;
        if (newHeight < 10) newHeight = 10;

        // Convert to percentages
        boxElement.style.left = `${(newLeft / overlayW) * 100}%`;
        boxElement.style.top = `${(newTop / overlayH) * 100}%`;
        boxElement.style.width = `${(newWidth / overlayW) * 100}%`;
        boxElement.style.height = `${(newHeight / overlayH) * 100}%`;

        setDragStart({ x, y });
      }
    }

    onMouseMove(x, y);
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    e.preventDefault();
    const { x, y } = getRelativeMousePos(e);

    if (isPanning) {
      setIsPanning(false);
      return;
    }

    if (isDrawingRef.current) {
      finishDrawing(x, y);
    } else if (isDragging && dragBoxId && !isDrawMode) {
      // Save box position: read resolved pixel position from DOM,
      // then convert to PDF coordinates for storage.
      const boxElement = overlayRef.current?.querySelector(`[data-box-id="${dragBoxId}"]`) as HTMLElement;
      if (boxElement) {
        const left = boxElement.offsetLeft;
        const top = boxElement.offsetTop;
        const width = boxElement.offsetWidth;
        const height = boxElement.offsetHeight;

        const pdfCoords = canvasCoordsToPdfCoords([left, top, left + width, top + height]);

        const updatedBox = {
          ...redactionBoxes[dragBoxId],
          bbox: pdfCoords
        };

        if (onBoxUpdate) {
          onBoxUpdate(dragBoxId, updatedBox);
        }
      }

      setIsDragging(false);
      setDragBoxId(null);
    } else if (isResizing && dragBoxId && !isDrawMode) {
      // Save resized box: read resolved pixel position from DOM,
      // then convert to PDF coordinates for storage.
      const boxElement = overlayRef.current?.querySelector(`[data-box-id="${dragBoxId}"]`) as HTMLElement;
      if (boxElement) {
        const left = boxElement.offsetLeft;
        const top = boxElement.offsetTop;
        const width = boxElement.offsetWidth;
        const height = boxElement.offsetHeight;

        const pdfCoords = canvasCoordsToPdfCoords([left, top, left + width, top + height]);

        const updatedBox = {
          ...redactionBoxes[dragBoxId],
          bbox: pdfCoords
        };

        if (onBoxUpdate) {
          onBoxUpdate(dragBoxId, updatedBox);
        }
      }

      setIsResizing(false);
      setResizeHandle(null);
      setDragBoxId(null);
    }

    onMouseUp(x, y);
  };

  // Handle canvas click for deselection
  const handleCanvasClick = (e: React.MouseEvent) => {
    // If clicking on canvas (not overlay), deselect all selected boxes only if not in draw mode
    if (selectedBoxIds.size > 0 && !isDrawMode) {
      onBoxSelect('', false);
    }
  };

  // Render PDF when dependencies change - with debouncing
  useEffect(() => {
    queueRenderPage();
  }, [pdfDoc, pageNum, scale]);

  // Re-render when browser zoom changes DPR, so the canvas resolution
  // matches the new device pixel ratio (avoids blurry/over-sharp rendering).
  useEffect(() => {
    const mql = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    const handler = () => {
      // DPR changed — force re-render to update canvas backing store resolution
      queueRenderPage();
    };
    mql.addEventListener('change', handler, { once: true });
    return () => mql.removeEventListener('change', handler);
  });

  // Re-render boxes when they change - but only if not rendering PDF
  useEffect(() => {
    if (!isRendering) {
      renderRedactionBoxes();
    }
  }, [redactionBoxes, selectedBoxIds, pageNum, isRendering]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (renderTimeoutRef.current) {
        clearTimeout(renderTimeoutRef.current);
      }
      if (renderTaskRef.current) {
        try { renderTaskRef.current.cancel(); } catch { /* ignore */ }
        renderTaskRef.current = null;
      }
    };
  }, []);

  // Global pan event listeners for smooth all-direction panning
  useEffect(() => {
    if (!isPanning) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      const container = document.querySelector('.pdf-viewer-container');
      if (container) {
        const dx = e.clientX - panStart.x;
        const dy = e.clientY - panStart.y;
        container.scrollLeft = panStart.scrollLeft - dx;
        container.scrollTop = panStart.scrollTop - dy;
      }
    };

    const handleGlobalMouseUp = () => {
      setIsPanning(false);
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isPanning, panStart]);

  if (!pdfDoc) {
    return <div className="no-pdf-loaded">{t('noPdfLoaded')}</div>;
  }

  return (
    <div
      className={`pdf-viewer-wrapper ${!isDrawMode ? 'pan-mode' : ''} ${isPanning ? 'panning' : ''}`}
    >
      <div ref={containerRef} className="pdf-canvas-container">
        <canvas
          ref={canvasRef}
          id="pdf-canvas"
          className="pdf-canvas"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onClick={handleCanvasClick}
        ></canvas>
        <div
          ref={overlayRef}
          id="redaction-overlay"
          className="redaction-overlay"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        ></div>
      </div>
    </div>
  );
};

export default PDFViewer;
